import {
  IconButton,
  Modal,
  SingleSelectDropdown,
  Text,
  TextInput,
  toast,
} from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  addTrainingGroupGithubTeam,
  createTrainingGroup,
  deleteTrainingGroup,
  getOrgDirectory,
  isDuplicateGithubTeamError,
  isDuplicateTrainingGroupError,
  listTrainingGroupCandidates,
  listTrainingGroups,
  removeTrainingGroupGithubTeam,
  updateTrainingGroup,
  type TrainingGroup,
  type TrainingGroupCandidate,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import styles from "./GroupsManagerModal.module.css";

export type GroupsManagerModalProps = {
  onClose: () => void;
  // 그룹·GitHub팀 매핑이 바뀌면 호출한다. 부모가 멤버 디렉터리·그룹 후보를 다시 조회한다.
  onChanged: () => void;
};

const UNSET = "";

// Registry 그룹 생성·삭제와 training Group 및 GitHub 팀 매핑을 관리한다. GitHub 팀은
// 조직 디렉터리에서 불러온 목록으로 선택한다.
export function GroupsManagerModal({ onClose, onChanged }: GroupsManagerModalProps): ReactNode {
  const [groups, setGroups] = useState<TrainingGroup[]>([]);
  const [candidates, setCandidates] = useState<TrainingGroupCandidate[]>([]);
  const [githubTeams, setGithubTeams] = useState<string[]>([]);
  // GitHub 팀 조회 실패는 그룹 목록을 막지 않는다. 팀 추가 드롭다운만 실패를 알린다.
  const [githubTeamsFailed, setGithubTeamsFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const dirtyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setGithubTeamsFailed(false);
    void Promise.allSettled([
      listTrainingGroups(),
      listTrainingGroupCandidates(),
      getOrgDirectory(),
    ]).then(([groupsResult, candidatesResult, directoryResult]) => {
      if (!alive) return;
      if (groupsResult.status === "fulfilled") {
        setGroups(groupsResult.value);
      } else {
        setLoadError(resolveErrorMessage(groupsResult.reason));
      }
      if (candidatesResult.status === "fulfilled") {
        setCandidates(candidatesResult.value);
      }
      if (directoryResult.status === "fulfilled") {
        setGithubTeams(directoryResult.value.teams.map((team) => team.github_team_name));
      } else {
        setGithubTeamsFailed(true);
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [reload]);

  const candidateOptions = [
    { value: UNSET, label: "미지정" },
    ...candidates.map((candidate) => ({ value: candidate.id, label: candidate.name })),
  ];

  async function run(action: () => Promise<void>): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      dirtyRef.current = true;
    } finally {
      setBusy(false);
    }
  }

  async function handleCreate(): Promise<void> {
    const name = newName.trim();
    if (name === "" || busy) return;
    setBusy(true);
    try {
      const created = await createTrainingGroup({ name });
      setGroups((prev) => [created, ...prev]);
      setNewName("");
      dirtyRef.current = true;
    } catch (error) {
      toast.error(
        isDuplicateTrainingGroupError(error)
          ? "이미 같은 이름의 그룹이 있습니다."
          : resolveErrorMessage(error),
      );
    } finally {
      setBusy(false);
    }
  }

  function handleSetCandidate(group: TrainingGroup, value: string): void {
    const trainingGroupId = value === UNSET ? null : value;
    if (trainingGroupId === group.training_group_id) return;
    void run(async () => {
      try {
        const updated = await updateTrainingGroup(group.uuid, {
          training_group_id: trainingGroupId,
        });
        setGroups((prev) => prev.map((entry) => (entry.uuid === group.uuid ? updated : entry)));
      } catch (error) {
        // training group 은 그룹 하나에만 연결된다(BE UNIQUE). 이미 연결된 후보를 고르면 409 다.
        toast.error(
          isDuplicateTrainingGroupError(error)
            ? "이 training group 은 다른 그룹에 이미 연결되어 있습니다. 그 그룹의 연결을 먼저 해제해 주세요."
            : resolveErrorMessage(error),
        );
      }
    });
  }

  function handleAddGithubTeam(group: TrainingGroup, githubTeamName: string): void {
    void run(async () => {
      try {
        const updated = await addTrainingGroupGithubTeam(group.uuid, githubTeamName);
        setGroups((prev) => prev.map((entry) => (entry.uuid === group.uuid ? updated : entry)));
      } catch (error) {
        toast.error(
          isDuplicateGithubTeamError(error)
            ? "이미 다른 그룹에 매핑된 GitHub 팀입니다."
            : resolveErrorMessage(error),
        );
      }
    });
  }

  function handleRemoveGithubTeam(group: TrainingGroup, githubTeamName: string): void {
    void run(async () => {
      await removeTrainingGroupGithubTeam(group.uuid, githubTeamName);
      setGroups((prev) =>
        prev.map((entry) =>
          entry.uuid === group.uuid
            ? {
                ...entry,
                github_team_names: entry.github_team_names.filter(
                  (name) => name !== githubTeamName,
                ),
              }
            : entry,
        ),
      );
    });
  }

  function handleDelete(group: TrainingGroup): void {
    void run(async () => {
      await deleteTrainingGroup(group.uuid);
      setGroups((prev) => prev.filter((entry) => entry.uuid !== group.uuid));
    });
  }

  function close(): void {
    if (dirtyRef.current) {
      onChanged();
    }
    onClose();
  }

  return (
    <Modal
      open
      onClose={close}
      size="md"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby="groups-manager-title"
    >
      <HatModalHeader
        title="Registry 그룹을 관리합니다."
        titleId="groups-manager-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <div className={styles.createRow}>
          <TextInput
            value={newName}
            placeholder="새 그룹 이름을 입력해 주세요."
            aria-label="새 그룹 이름"
            disabled={busy}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setNewName(event.target.value)}
          />
          <IconButton
            text="추가"
            variant="solid"
            disabled={busy || newName.trim() === ""}
            onClick={() => void handleCreate()}
          />
        </div>

        {loading ? (
          <LoadingState>그룹을 불러오는 중입니다.</LoadingState>
        ) : loadError ? (
          <EmptyState
            tone="error"
            action={{ label: "다시 시도", onClick: () => setReload((value) => value + 1) }}
          >
            {loadError}
          </EmptyState>
        ) : groups.length === 0 ? (
          <EmptyState>아직 그룹이 없습니다. 위에서 새 그룹을 만들어 보세요.</EmptyState>
        ) : (
          <div className={styles.groupList}>
            {groups.map((group) => (
              <GroupCard
                key={group.uuid}
                group={group}
                candidateOptions={candidateOptions}
                githubTeams={githubTeams}
                githubTeamsFailed={githubTeamsFailed}
                busy={busy}
                onSetCandidate={handleSetCandidate}
                onAddGithubTeam={handleAddGithubTeam}
                onRemoveGithubTeam={handleRemoveGithubTeam}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

type GroupCardProps = {
  group: TrainingGroup;
  candidateOptions: readonly { value: string; label: string }[];
  githubTeams: readonly string[];
  githubTeamsFailed: boolean;
  busy: boolean;
  onSetCandidate: (group: TrainingGroup, value: string) => void;
  onAddGithubTeam: (group: TrainingGroup, githubTeamName: string) => void;
  onRemoveGithubTeam: (group: TrainingGroup, githubTeamName: string) => void;
  onDelete: (group: TrainingGroup) => void;
};

function GroupCard({
  group,
  candidateOptions,
  githubTeams,
  githubTeamsFailed,
  busy,
  onSetCandidate,
  onAddGithubTeam,
  onRemoveGithubTeam,
  onDelete,
}: GroupCardProps): ReactNode {
  const mapped = new Set(group.github_team_names);
  const addOptions = githubTeams
    .filter((name) => !mapped.has(name))
    .map((name) => ({ value: name, label: name }));

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupHeader}>
        <Text variant={hatcheryTextModes["body-bold"]}>{group.name}</Text>
        <IconButton
          text="삭제"
          variant="surface"
          size="sm"
          tone="danger"
          disabled={busy}
          onClick={() => onDelete(group)}
        />
      </div>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="training-infra Group"
        value={group.training_group_id ?? UNSET}
        options={candidateOptions}
        disabled={busy}
        onValueChange={(value) => onSetCandidate(group, value)}
      />
      <div className={styles.githubTeams}>
        {group.github_team_names.length > 0
          ? group.github_team_names.map((name) => (
              <span key={name} className={styles.githubChip}>
                <Text variant={hatcheryTextModes["body-normal"]}>{name}</Text>
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`${name} GitHub 팀 매핑 제거`}
                  disabled={busy}
                  onClick={() => onRemoveGithubTeam(group, name)}
                >
                  ×
                </button>
              </span>
            ))
          : null}
      </div>
      <SingleSelectDropdown
        mode={hatcheryDropdownModes.default}
        label="GitHub 팀 매핑 추가"
        value={null}
        options={addOptions}
        placeholder={
          // 받지 못한 목록을 "없습니다" 로 알리지 않는다.
          githubTeamsFailed
            ? "GitHub 팀을 불러오지 못했습니다. 창을 닫았다가 다시 열어 주세요."
            : addOptions.length === 0
              ? "추가할 GitHub 팀이 없습니다."
              : "GitHub 팀을 선택해 주세요."
        }
        disabled={busy || addOptions.length === 0}
        onValueChange={(value) => onAddGithubTeam(group, value)}
      />
    </div>
  );
}

function resolveErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "그룹 요청을 완료하지 못했습니다.";
}
